// Seguimiento del cobro manual (Riesgo 3), hasta que la Fase 5 lo automatice. Un mes sin cobrar no cuenta como
// renovación, así que lo que importa no es cuánto se cobró sino cuántos renuevan.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Cargando, Input, Modal, PageHead, Select, Tabla, TarjetaIndicador, Vacio } from '@glubi/ds';
import { api, mensajeDeError } from '../lib/api';
import { pesos } from '../lib/formato';
import { useSesion } from '../lib/sesion';

const ESTADO_COBRO: Record<string, { label: string; estado: any }> = {
  por_cobrar: { label: 'Por cobrar', estado: 'neutral' },
  cobrado: { label: 'Cobrado', estado: 'pending' },
  pagado: { label: 'Pagado', estado: 'ok' },
  no_pago: { label: 'No pagó', estado: 'error' },
};

export function Cobro() {
  const qc = useQueryClient();
  const { aviso } = useSesion();
  const q = useQuery({ queryKey: ['panel-cobros'], queryFn: () => api<any[]>('/api/panel/cobros') });
  const [nuevo, setNuevo] = useState(false);
  const [f, setF] = useState({ periodo: new Date().toISOString().slice(0, 7), precioPorSolucion: '60000' });

  const abrir = useMutation({
    mutationFn: () => api('/api/panel/cobros/periodo', { cuerpo: { periodo: f.periodo, precioPorSolucion: Number(f.precioPorSolucion) } }),
    onSuccess: (r: any) => { aviso(`${r.abiertos} negocios en el periodo`); setNuevo(false); void qc.invalidateQueries({ queryKey: ['panel-cobros'] }); },
    onError: (e) => aviso(mensajeDeError(e)),
  });
  const cambiar = useMutation({
    mutationFn: ({ id, estado }: { id: string; estado: string }) => api(`/api/panel/cobros/${id}`, { cuerpo: { estado } }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['panel-cobros'] }),
    onError: (e) => aviso(mensajeDeError(e)),
  });

  if (q.isLoading) return <Cargando />;
  const filas = q.data ?? [];
  const delMes = filas.filter((c) => c.periodo === (filas[0]?.periodo ?? ''));
  const pagados = delMes.filter((c) => c.estado === 'pagado').length;

  return (
    <div className="page">
      <PageHead title="Cobro" subtitle="Quién pagó y quién debe, por periodo. El cobro es real desde el Lanzamiento y manual hasta la Fase 5." />
      <div className="kpis">
        <TarjetaIndicador label="Periodo abierto" value={filas[0]?.periodo ?? '—'} trend={`${delMes.length} negocios`} />
        <TarjetaIndicador label="Pagaron" value={delMes.length ? `${Math.round((pagados / delMes.length) * 100)}%` : '—'} tone={delMes.length && pagados / delMes.length >= 0.6 ? 'ok' : 'pending'} trend={`${pagados} de ${delMes.length} · umbral 60% el mes 2`} />
        <TarjetaIndicador label="Por cobrar" value={pesos(delMes.filter((c) => c.estado !== 'pagado').reduce((s, c) => s + Number(c.monto), 0))} trend="Cobro manual hasta la Fase 5" />
      </div>
      <div style={{ marginBottom: 14 }}><Button icon="plus" onClick={() => setNuevo(true)}>Abrir un periodo</Button></div>

      {!filas.length ? <Vacio titulo="Todavía no hay cobros">El cobro real empieza en el Lanzamiento, y es manual hasta la Fase 5.</Vacio> : (
        <Tabla rowKey="id" rows={filas}
          columns={[
            { key: 'periodo', label: 'Periodo' },
            { key: 'negocio', label: 'Negocio' },
            { key: 'soluciones_activas', label: 'Soluciones', numeric: true },
            { key: 'monto', label: 'Monto', numeric: true, render: (c: any) => pesos(c.monto) },
            { key: 'factura', label: 'Factura', render: (c: any) => c.factura ?? <span className="muted">—</span> },
            { key: 'estado', label: 'Estado', render: (c: any) => <Badge state={ESTADO_COBRO[c.estado]?.estado}>{ESTADO_COBRO[c.estado]?.label}</Badge> },
            {
              key: 'acciones', label: '', render: (c: any) => (
                <Select value={c.estado} options={Object.entries(ESTADO_COBRO).map(([v, x]) => ({ value: v, label: x.label }))} onChange={(e) => cambiar.mutate({ id: c.id, estado: e.target.value })} />
              ),
            },
          ]} />
      )}

      {nuevo && (
        <Modal title="Abrir un periodo de cobro" subtitle="Una fila por negocio, con el número de soluciones activas y el precio por solución." onClose={() => setNuevo(false)}
          footer={<Button onClick={() => abrir.mutate()} disabled={abrir.isPending}>Abrir</Button>}>
          <Input label="Periodo" value={f.periodo} onChange={(e) => setF({ ...f, periodo: e.target.value })} helper="AAAA-MM" />
          <Input label="Precio por solución activa" value={f.precioPorSolucion} onChange={(e) => setF({ ...f, precioPorSolucion: e.target.value })} unit="COP" inputMode="numeric" />
        </Modal>
      )}
    </div>
  );
}
